<template>
  <v-snackbar :value="showPulseAlert" color="warning" :timeout="-1" top class="wims-pulse-snackbar">
    <div class="d-flex align-center">
      <span style="max-width: 350px">
        {{ $t('Heads up! Scheduled System Maintenance. Some features may be temporarily unavailable.') }}
      </span>
      <v-tooltip v-if="pulseCommitMessages?.length" bottom max-width="400" :z-index="9999">
        <template #activator="{ on, attrs }">
          <v-icon class="ml-2" v-bind="attrs" v-on="on">
            {{ icons.mdiSourceCommit }}
          </v-icon>
        </template>
        <div v-for="(msg, i) in pulseCommitMessages" :key="i" class="text-caption">• {{ msg }}</div>
      </v-tooltip>
    </div>
    <template #action>
      <v-btn icon small @click="dismissAlert">
        <v-icon small>{{ icons.mdiClose }}</v-icon>
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import { mdiClose, mdiSourceCommit } from '@mdi/js';
import Dataman from 'App/Dataman';
import { ROLE_ADMIN } from 'App/constants/Roles';
import RBACMixin from 'App/mixins/RBACMixin';

const PULSE_WINDOWS = [
  { start: 12 * 60 + 30, end: 14 * 60 },
  { start: 18 * 60, end: 23 * 60 },
];
const POLLING_INTERVAL = 5 * 60 * 1000;

export default {
  name: 'WimsPulseAlert',
  mixins: [RBACMixin],
  data() {
    return {
      icons: { mdiClose, mdiSourceCommit },
      wimsPulseData: null,
      wimsPulseDismissed: false,
      wimsPulseInWindow: false,
      pulsePollingTimer: null,
      pulseWindowTimer: null,
    };
  },
  computed: {
    pulseService() {
      return this.wimsPulseData?.services.find((service) => service.active_technical_work) ?? {};
    },
    showPulseAlert() {
      if (this.wimsPulseDismissed) return false;
      const svc = this.pulseService;
      if (!svc || !svc.current_status) return false;
      return svc.active_technical_work && ['IN_PROGRESS', 'FAILED'].includes(svc.current_status);
    },
    pulseCommitMessages() {
      if (!this.hasRole(ROLE_ADMIN)) return [];
      return this.pulseService?.commit_messages || [];
    },
  },
  methods: {
    async fetchPulseStatus() {
      try {
        const prevStatus = this.pulseService?.current_status;
        const res = await Dataman.DB.wimsPulse.getStatus();
        this.wimsPulseData = res.data;
        if (this.pulseService?.current_status !== prevStatus) {
          this.wimsPulseDismissed = false;
        }
      } catch (e) {
        // silently ignore connection errors
      }
    },
    isInPulseWindow() {
      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      return PULSE_WINDOWS.some((w) => minutes >= w.start && minutes < w.end);
    },
    startPulsePolling() {
      if (this.pulsePollingTimer) return;
      this.pulsePollingTimer = setInterval(this.fetchPulseStatus, POLLING_INTERVAL);
    },
    stopPulsePolling() {
      clearInterval(this.pulsePollingTimer);
      this.pulsePollingTimer = null;
    },
    dismissAlert() {
      this.stopPulsePolling();
      clearInterval(this.pulseWindowTimer);
      this.pulseWindowTimer = null;
      this.wimsPulseData = null;
      this.wimsPulseDismissed = true;
    },
    initPulseMonitoring() {
      this.wimsPulseInWindow = this.isInPulseWindow();
      if (this.wimsPulseInWindow) {
        this.fetchPulseStatus();
        this.startPulsePolling();
      }
      this.pulseWindowTimer = setInterval(() => {
        this.wimsPulseInWindow = this.isInPulseWindow();
        if (this.wimsPulseInWindow) {
          if (!this.pulsePollingTimer) {
            this.fetchPulseStatus();
            this.startPulsePolling();
          }
        } else {
          this.stopPulsePolling();
          this.wimsPulseData = null;
          this.wimsPulseDismissed = false;
        }
      }, 60 * 1000);
    },
  },
  created() {
    this.initPulseMonitoring();
  },
  beforeDestroy() {
    this.stopPulsePolling();
    clearInterval(this.pulseWindowTimer);
  },
};
</script>

<style scoped lang="scss">
:deep(.wims-pulse-snackbar .v-snack__wrapper) {
  min-width: auto;
  max-width: 480px;
}
</style>
